import { useMemo } from "react";
import Banner from "@/components/Banner";
import Table, { TableControls, TableFooter } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import SearchInput from "@/components/SearchInput";
import type { MaterialNames } from "@/components/material_symbol_names";
import type { TableRowMenu } from "@/hooks/useTableRowMenu";
import type { CoverageGroupBy, CoverageGroupMember, CoverageGroupRow } from "../types";
import { getCoverageGroupColumns } from "./GroupedCoverageColumns";

type GroupedCoverageTableProps = {
  groups: CoverageGroupRow[];
  loading: boolean;
  truncated?: boolean;
  groupBy: CoverageGroupBy;
  groupColumnHeader: string;
  addActionLabel: string;
  memberAddActionLabel: string;
  removeActionLabel: string;
  removeActionIconName?: MaterialNames;
  searchPlaceholder: string;
  searchValue: string;
  hasActiveFilters: boolean;
  onSearchChange: (value: string) => void;
  onSearchClear: () => void;
  tableTestId: string;
  emptyTitle: string;
  emptyCaption?: string;
  emptyIcon: MaterialNames;
  setMenu: (menu: TableRowMenu) => void;
  onAdd: (group: CoverageGroupRow) => void;
  onAddMember: (member: CoverageGroupMember) => void;
  onRemoveMember: (member: CoverageGroupMember) => void;
};

export default function GroupedCoverageTable({
  groups,
  loading,
  truncated = false,
  groupBy,
  groupColumnHeader,
  addActionLabel,
  memberAddActionLabel,
  removeActionLabel,
  removeActionIconName = "delete",
  searchPlaceholder,
  searchValue,
  hasActiveFilters,
  onSearchChange,
  onSearchClear,
  tableTestId,
  emptyTitle,
  emptyCaption,
  emptyIcon,
  setMenu,
  onAdd,
  onAddMember,
  onRemoveMember
}: GroupedCoverageTableProps) {
  const columns = useMemo(
    () =>
      getCoverageGroupColumns({
        groupBy,
        groupColumnHeader,
        addActionLabel,
        memberAddActionLabel,
        removeActionLabel,
        removeActionIconName,
        setMenu,
        onAdd,
        onAddMember,
        onRemoveMember
      }),
    [
      addActionLabel,
      groupBy,
      groupColumnHeader,
      memberAddActionLabel,
      onAdd,
      onAddMember,
      onRemoveMember,
      removeActionIconName,
      removeActionLabel,
      setMenu
    ]
  );

  return (
    <div className="flex flex-col gap-6">
      {truncated ? (
        <Banner
          variant="warn"
          description="Only the first 100 records loaded. More exist that are not shown."
          hideSecondaryDismiss
        />
      ) : null}
      <Table
        data={groups}
        loading={loading}
        columns={columns}
        data-testid={tableTestId}
        hasActiveFilters={hasActiveFilters}
        renderFilteredEmptyState={() => (
          <EmptyState title="No matches found." caption="Try a different search" iconName={emptyIcon} />
        )}
        renderNoDataEmptyState={() => <EmptyState title={emptyTitle} caption={emptyCaption} iconName={emptyIcon} />}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls onSortClick={onSortClick}>
            <SearchInput
              placeholder={searchPlaceholder}
              query={searchValue}
              onClear={onSearchClear}
              onChange={onSearchChange}
            />
          </TableControls>
        )}
        renderFooter={() => <TableFooter totalRowCount={groups.length} />}
      />
    </div>
  );
}
